import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export default function QualificationPassword() {
    const location = useLocation();
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [status, setStatus] = useState("idle");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const email = location.state?.email || "";

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!password || !confirmPassword) {
            setStatus("error");
            return;
        }

        if (password !== confirmPassword) {
            setStatus("mismatch");
            return;
        }

        setLoading(true);
        setStatus("idle");

        try {
            const response = await fetch("https://api.insightos.co.uk/api/leads/qualification/password", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password }),
            });

            const data = await response.json();

            if (!response.ok) {
                setStatus("error");
                return;
            }

            setResult({ companyCode: data.companyCode, credits: data.credits ?? 10 });
            setStatus("success");
        } catch {
            setStatus("error");
        } finally {
            setLoading(false);
        }
    };

    if (status === "success" && result) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-violet-900 via-indigo-900 to-blue-900 px-4 py-10"
            >
                <div className="w-full max-w-md text-center">
                    <img src="/avenor.png" alt="Avenor" className="mx-auto mb-8 w-28 drop-shadow-xl" />

                    <div className="relative overflow-hidden rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 p-8 shadow-2xl">
                        {/* Success icon */}
                        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-emerald-500 shadow-lg">
                            <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>

                        <h2 className="mb-1 text-2xl font-bold text-white">You're all set!</h2>
                        <p className="mb-6 text-sm text-indigo-200">Your account is ready. Here are your details.</p>

                        {/* CustomerID card */}
                        <div className="mb-4 rounded-xl bg-white/10 border border-white/20 p-4">
                            <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-indigo-300">Your Customer ID</p>
                            <p className="text-2xl font-bold tracking-wider text-white">{result.companyCode}</p>
                            <p className="mt-1 text-xs text-indigo-300">Keep this safe — you'll need it to log in.</p>
                        </div>

                        {/* Credits badge */}
                        <div className="mb-6 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-500/30 to-indigo-500/30 border border-violet-400/30 px-4 py-3">
                            <span className="text-xl">🎁</span>
                            <div className="text-left">
                                <p className="text-sm font-semibold text-white">{result.credits} free credits added</p>
                                <p className="text-xs text-indigo-300">Ready to use on your account now</p>
                            </div>
                        </div>

                        <p className="mb-6 text-xs text-indigo-300">
                            A confirmation email has been sent to <span className="font-semibold text-white">{email}</span> with your details.
                        </p>

                        <button
                            onClick={() => navigate("/login")}
                            className="w-full rounded-xl bg-gradient-to-r from-violet-500 to-indigo-600 px-6 py-3 text-base font-semibold text-white shadow-lg hover:from-violet-400 hover:to-indigo-500 focus:outline-none focus:ring-2 focus:ring-violet-400 transition-all duration-150"
                        >
                            Go to Login →
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen flex-col bg-no-repeat bg-center bg-cover items-center justify-center bg-gradient-to-br from-violet-900 via-indigo-900 to-blue-900 px-4 py-10"
        style={{ backgroundImage: "url('/nologo.png')" }}>

            <div className="w-full max-w-md">
                <div className="mb-8 text-center">
                    <img src="/avenor.png" alt="Avenor" className="mx-auto mb-5 w-28 drop-shadow-xl" />
                    <h1 className="text-3xl font-bold text-white">Create your password</h1>
                    <p className="mt-2 text-sm text-white">Your application was approved. Set a password to activate your account.</p>
                    {email && (
                        <p className="mt-2 text-xs font-medium text-indigo-900">{email}</p>
                    )}
                </div>

                <div className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 p-8 shadow-2xl">
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="mb-1.5 block text-sm font-medium text-indigo-200" htmlFor="password">
                                Password
                            </label>
                            <input
                                id="password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter a strong password"
                                className="block w-full rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm text-white placeholder-indigo-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400/50 transition"
                            />
                        </div>

                        <div>
                            <label className="mb-1.5 block text-sm font-medium text-indigo-200" htmlFor="confirmPassword">
                                Confirm Password
                            </label>
                            <input
                                id="confirmPassword"
                                type="password"
                                required
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Re-enter your password"
                                className="block w-full rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm text-white placeholder-indigo-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400/50 transition"
                            />
                        </div>

                        {status === "mismatch" && (
                            <div className="flex items-center gap-2 rounded-lg bg-red-500/20 border border-red-400/30 px-3 py-2">
                                <span className="text-red-300 text-sm">Passwords do not match.</span>
                            </div>
                        )}
                        {status === "error" && (
                            <div className="flex items-center gap-2 rounded-lg bg-red-500/20 border border-red-400/30 px-3 py-2">
                                <span className="text-red-300 text-sm">Something went wrong. Please try again.</span>
                            </div>
                        )}

                        {/* Bonus credits callout */}
                        <div className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-500/20 to-indigo-500/20 border border-violet-400/30 px-4 py-3">
                            <span className="text-lg">🎁</span>
                            <p className="text-xs text-indigo-200">You'll receive <strong className="text-white">10 free credits</strong> when you activate your account.</p>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full rounded-xl bg-gradient-to-r from-violet-500 to-indigo-600 px-6 py-3 text-base font-semibold text-white shadow-lg hover:from-violet-400 hover:to-indigo-500 focus:outline-none focus:ring-2 focus:ring-violet-400 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150"
                        >
                            {loading ? "Activating..." : "Activate Account"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
